import * as THREE from "three";
import { DECOS, pickDeco, scatterTable, type ScatterPalette } from "./deco/index.ts";
import { buildDeco } from "./deco/define.ts";
import type { DecoSpec, Habitat } from "./deco/define.ts";
import { rand } from "./color.ts";
import { buildTerrain, buildWater, heightAt, WATER_LEVEL, setTerrainRoadPaint } from "./terrain.ts";
import { buildInstanced, VARIANTS, type Placement, type CulledDeco } from "./instanced.ts";
import { MAP_SIZE } from "./map/active.ts";
import { loadedRoads, loadedMap, scatterPaletteById } from "./content/load.ts";
import type { MapDoc, RoadStyle } from "./content/schemas.ts";
import { setWorldNav, type Collider, type BoxCollider, type Surface } from "./physics.ts";
import { buildStructure, type InteriorInfo, type BuiltStructure, type Seat, type StructureDoor } from "./structure/define.ts";
import { variantSeed } from "./model/variants.ts";
import { resolvePlacements, type StructurePlacement } from "./structure/index.ts";
import { setStructureFlatZones, type FlatZone } from "./flatten.ts";
import { buildPath, type Waypoint } from "./path.ts";
import { hydrateMasks, hydrateBrush, brushDelta, maskDensity, maskWeights, segDist, type HydratedMask, type BrushGrid } from "./map/layers.ts";

// ---- Roads ----
// Every road is a polyline of [x,z] waypoints (+ optional width + style).
// One archetype (verge / onRoad boost); two looks: city (cobble mesh) and dirt
// (terrain vertex paint + pebbles). Author in map-doc `roads[]` or #/world.
// ROADS here is only the live active-map mirror, not the authoring target.
export type { RoadStyle };
export type Road = { waypoints: Waypoint[]; width?: number; style?: RoadStyle };

// Roads are CONTENT: polylines live in the map doc's `roads` (content/maps,
// hydrated by src/content/load.ts). The ACTIVE map's roads (plan 21): seeded
// with the default map's, refreshed by buildWorld(map) — onRoad (the client's
// road speed boost) reads the built map.
export const ROADS: Road[] = [...(loadedRoads as Road[])];

// Shortest distance from point (px,pz) to the segment a→b — the shared
// segDist (map/layers.ts, ridge/ramp features use the same math; returns a
// reused scratch, consumed immediately here). Used to keep deco scatter off
// the roads so the walkways stay clear.
function distToSeg(px: number, pz: number, ax: number, az: number, bx: number, bz: number): number {
  return segDist(px, pz, ax, az, bx, bz).d;
}

// True if (x,z) lies on or beside any road — trees/rocks/etc. must not spawn
// there or they'd block the path. Clearance = half the path width + a verge margin
// (so the canopy/footprint of a deco stays off the cobbles, not just its centre).
// Parametric over the road list so the studio world editor can test against its
// EDITED roads, not the booted content.
function nearRoadIn(roads: Road[], x: number, z: number): boolean {
  for (const road of roads) {
    const clear = (road.width ?? 3.0) / 2 + 2.5;
    const wp = road.waypoints;
    for (let i = 0; i < wp.length - 1; i++) {
      if (distToSeg(x, z, wp[i][0], wp[i][1], wp[i + 1][0], wp[i + 1][1]) < clear) return true;
    }
  }
  return false;
}

// True if (x,z) sits on the walkable surface of any road (within half its width,
// plus a small tolerance so the very edge still counts). Used to reward walking on
// roads with a speed/stamina bonus — unlike nearRoad (a wider verge for deco
// exclusion), this is the actual paved footprint.
export function onRoad(x: number, z: number): boolean {
  for (const road of ROADS) {
    const half = (road.width ?? 3.0) / 2 + 0.4;
    const wp = road.waypoints;
    for (let i = 0; i < wp.length - 1; i++) {
      if (distToSeg(x, z, wp[i][0], wp[i][1], wp[i + 1][0], wp[i + 1][1]) < half) return true;
    }
  }
  return false;
}


// Structures stay as real Groups (not instanced) so they can carry point lights.
// Exported for the studio world editor's scatter preview (same split).
export const NON_INSTANCED = new Set(["well"]);

/** A point of interest for the minimap. `kind` lets the minimap weight what it
 * shows — structures + roads are drawn prominently; scattered decos are muted. */
export type MapDot = { x: number; z: number; color: number; kind?: "deco" | "structure" | "road" };
/** A warm light source emitted by a lit structure (well). */
export type LightSpot = { x: number; y: number; z: number };

/** A circular no-go zone around a placed structure (castle/cottages). Published by
 *  `buildWorld` so the server can keep roaming wildlife from picking waypoints inside
 *  the castle or a cottage — the roam rect is a single box and can't express holes. */
export type KeepOut = { x: number; z: number; r: number };
export const structureKeepOuts: KeepOut[] = [];

/** One interactable prop in the world — a scattered deco whose spec carries an
 *  `interact` block (tree/flower/signpost), or an authored placement from
 *  content/world/props.json (chests). Published as `world.userData.interactables`
 *  and derived IDENTICALLY on both sides (the scatter is deterministic from the
 *  seed), so the server can validate an `interact` intent by id alone.
 *
 *  Scattered ids are `dc<grid-counter>` — stable for a given seed + content set,
 *  but they DRIFT if the scatter inputs change (new deco types, weight edits).
 *  That only mis-keys transient state (a hidden flower for ≤ its respawn window);
 *  durable chest state uses the AUTHORED props.json ids, which never drift. */
export type WorldInteractable = {
  id: string;
  spec: DecoSpec; // carries .interact (+ radius for the reach check)
  x: number; y: number; z: number;
  scale: number;
  /** True for authored props.json placements (rendered as real Groups). */
  placed: boolean;
};

/** One placed structure, built: the placement, its geometry, and the flatten
 *  pad derived from it. Returned by buildStructurePlacements so buildWorld and
 *  the studio world editor share the exact pad math. */
export type PlacedStructure = {
  placement: StructurePlacement;
  built: BuiltStructure;
  baseY: number;
  footprint: number;
  zone: FlatZone;
};

/** Phase 1 of a world build: build each placed structure on the natural grade,
 *  deriving its flatten pad IN ORDER — a later placement's default baseY reads
 *  the earlier pads, so the pads are registered incrementally via
 *  setStructureFlatZones as we go. THE one home for this derivation: buildWorld
 *  and the studio world editor both call it, so the editor's terrain preview
 *  can never disagree with the game's ground. */
export function buildStructurePlacements(
  placements: StructurePlacement[],
): PlacedStructure[] {
  const out: PlacedStructure[] = [];
  const flatZones: FlatZone[] = [];
  // ALWAYS publish the pad list first — including the empty list. buildMapRts
  // builds every map in sequence and captures currentStructureFlatZones() into
  // each MapRt's TerrainCtx; a map with ZERO structures must not inherit the
  // previous map's pads (that leaked overworld/tutorial house pads onto
  // tutorial-south/west, so the server sim stood NPCs on phantom flat ground
  // while the client terrain — built in a different order — still showed the
  // real hills/basins, and wildlife floated or sank). Same clear-then-fill
  // discipline as structureKeepOuts below.
  setStructureFlatZones(flatZones);
  for (const p of placements) {
    const turns = p.turns ?? 0;
    // baseY defaults to the (already-flattened) terrain height here, so a building
    // rests on the ground instead of forcing a deep pad down at y=0. Earlier
    // structures' pads are already registered, so later ones read off them.
    const baseY = p.baseY ?? heightAt(p.x, p.z);
    const built = buildStructure(p.spec, p.x, p.z, baseY, turns);
    // Footprint = half-diagonal of the interior AABB + margin, unless overridden.
    const it = built.interior;
    const autoFoot = Math.hypot(it.maxX - it.minX, it.maxZ - it.minZ) / 2 + 1.5;
    const footprint = p.footprint ?? autoFoot;
    const zone: FlatZone = {
      x: p.x, z: p.z,
      r: p.flatten?.r ?? footprint,
      feather: p.flatten?.feather ?? 9,
      y: p.flatten?.y ?? baseY - 0.12, // grass tucks just under the stonework
    };
    flatZones.push(zone);
    setStructureFlatZones(flatZones); // register as we go so later baseY's see it
    out.push({ placement: p, built, baseY, footprint, zone });
  }
  return out;
}

/** One scatter decision — pure DATA (no THREE). buildWorld turns the list into
 *  instanced buffers / Groups / colliders; the studio world editor renders the
 *  same list for its preview, so the two can never disagree about what grows
 *  where. Deterministic from `seed` (+ the current heightAt). */
export type ScatterPlacement = {
  spec: DecoSpec;
  x: number; y: number; z: number;
  habitat: Habitat;
  rot: number;
  scale: number;
  /** Instanced mesh variant index (rand(s+7)). */
  variant: number;
  /** Interactable scatter id (`dc<grid-counter>`) when the spec interacts. */
  iid?: string;
  /** The cell's rng seed — NON_INSTANCED builds reseed from it (seed + 4). */
  seed: number;
};

// Solid-vs-solid spatial hash for scatter: grid step 7 + jitter can put two
// cell centres nearly on top of each other, and without this keep-out a tree
// trunk lands inside a rock/boulder. Soft cover (grass/flower/…) still nests.
// Cell size 4 + 2-cell pad covers rA+rB up to ~8 (boulder 2.0·1.25 + tree 1.6·1.25 ≈ 4.5).
const SOLID_CELL = 4;
const SOLID_PAD = 2;
type SolidRec = { x: number; z: number; r: number };

function solidOverlaps(
  buckets: Map<string, SolidRec[]>,
  x: number, z: number, r: number,
): boolean {
  const ix = Math.floor(x / SOLID_CELL);
  const iz = Math.floor(z / SOLID_CELL);
  for (let dx = -SOLID_PAD; dx <= SOLID_PAD; dx++) {
    for (let dz = -SOLID_PAD; dz <= SOLID_PAD; dz++) {
      const list = buckets.get(`${ix + dx},${iz + dz}`);
      if (!list) continue;
      for (const s of list) {
        if (Math.hypot(x - s.x, z - s.z) < r + s.r) return true;
      }
    }
  }
  return false;
}

function solidInsert(buckets: Map<string, SolidRec[]>, x: number, z: number, r: number): void {
  const key = `${Math.floor(x / SOLID_CELL)},${Math.floor(z / SOLID_CELL)}`;
  let list = buckets.get(key);
  if (!list) buckets.set(key, (list = []));
  list.push({ x, z, r });
}

/** The deco scatter loop, extracted (plan 20) so the studio world editor can
 *  recompute it against EDITED terrain/masks/roads. With no masks the density
 *  gate reduces to the old `rand(s) > 0.5` skip; solid-vs-solid keep-out may
 *  still drop a candidate after pick. `dc<i>` ids ride the grid counter, which
 *  increments every cell regardless, so a mask/terrain/solid-reject edit only
 *  re-keys cells whose OUTCOME changes (the documented transient-state drift,
 *  world.ts header). */
export function computeScatter(
  seed: number,
  opts: {
    roads?: Road[];
    masks?: HydratedMask[];
    /** Painted density field (MapDoc.scatterBrush) — multiplies the keep-rate
     *  by max(0, 1 + delta) per cell, on top of the masks. */
    scatterBrush?: BrushGrid;
    /** Composition override (MapDoc.scatterPalette) — restricts + reweights the
     *  eligible deco types map-wide. Omitted → the classic weight>0 pool. */
    palette?: ScatterPalette;
    /** Map side length the grid spans (default: the active map's). */
    size?: number;
    /** Structure-footprint test (buildWorld passes its built list). */
    clearOf?: (x: number, z: number) => boolean;
    /** Authored-deco clearing test. */
    nearProp?: (x: number, z: number) => boolean;
  } = {},
): ScatterPlacement[] {
  const roads = opts.roads ?? ROADS;
  const masks = opts.masks ?? [];
  const brush = opts.scatterBrush;
  const table = scatterTable(opts.palette);
  const out: ScatterPlacement[] = [];
  // Accepted solid footprints (trees/rocks/boulders/…) — reject later solids
  // whose collider circles would intersect (same r = radius·scale as physics).
  const solidBuckets = new Map<string, SolidRec[]>();
  // step 7 (was 6 at size 360): at 1080 the grid is ~154² cells → ~12k placed
  // decos (scales with map AREA — 2160 ≈ ~46k). Only the instances within view
  // distance render (the near-set refresh in instanced.ts), so density mostly
  // costs memory/build time now — but LOCAL density is still per-frame GPU
  // cost, so keep it in check.
  const step = 7;
  const half = (opts.size ?? MAP_SIZE) / 2 - step;
  let i = 0;
  for (let gx = -half; gx <= half; gx += step) {
    for (let gz = -half; gz <= half; gz += step) {
      i++;
      const s = seed * 1000 + i;
      const x = gx + (rand(s + 1) - 0.5) * step;
      const z = gz + (rand(s + 2) - 0.5) * step;
      // Density gate: the base 50% keep-rate scaled by any scatter masks AND
      // the painted density field (both multiply; rand() is a stateless hash,
      // so sampling x/z first changes nothing).
      let density = masks.length ? maskDensity(x, z, masks) : 1;
      if (brush) density *= Math.max(0, 1 + brushDelta(x, z, brush));
      if (rand(s) > 0.5 * density) continue;
      if (Math.hypot(x, z) < 12) continue; // keep the spawn clearing open
      if (opts.clearOf?.(x, z)) continue; // and keep structures' footprints clear
      if (nearRoadIn(roads, x, z)) continue; // and keep the roads clear of decos
      if (opts.nearProp?.(x, z)) continue; // and a clearing around each placed prop

      const y = heightAt(x, z);

      // Classify the spot into a habitat zone by its elevation vs. the waterline.
      let habitat: Habitat;
      let placeY: number;
      if (y < WATER_LEVEL - 0.4) { habitat = "water"; placeY = WATER_LEVEL; }
      else if (y < WATER_LEVEL + 0.9) { habitat = "shore"; placeY = y; }
      else { habitat = "land"; placeY = y; }

      const weights = masks.length ? maskWeights(x, z, masks) : null;
      const spec = pickDeco(table[habitat], rand(s + 3), weights ?? undefined);
      if (!spec) continue;

      const rot = rand(s + 5) * Math.PI * 2;
      const scale = 0.85 + rand(s + 6) * 0.4; // per-instance size variation

      // Solid-vs-solid: no tree-in-rock / stacked boulders. Soft cover still
      // places freely next to (and under) solids.
      if (spec.solid) {
        const r = spec.radius * scale;
        if (solidOverlaps(solidBuckets, x, z, r)) continue;
        solidInsert(solidBuckets, x, z, r);
      }

      // Interactable scatter (tree/flower/signpost): a deterministic id — the
      // grid counter is identical on both sides — keys picking/hiding on the
      // client and the server's `interact` validation. See WorldInteractable.
      const iid = spec.interact ? `dc${i}` : undefined;
      const variant = Math.floor(rand(s + 7) * VARIANTS);
      out.push({ spec, x, y: placeY, z, habitat, rot, scale, variant, iid, seed: s });
    }
  }
  return out;
}

/** Build a map doc into terrain + water + scattered decos (plan 21: takes the
 *  whole DOC — size, seed, structures, roads, decos, items all come from it;
 *  defaults to the default map). Deterministic from the doc, so client,
 *  server, and studio build the same world with no literal to keep in sync.
 *  PRE-CONDITION: the doc is the ACTIVE map (content/load.ts activateMap) —
 *  heightAt must compose ITS terrain layers before any sample here. */
/** Freeze a fully-built static subtree: bake each local matrix once and stop
 *  three recomposing every one of them per frame (matrixAutoUpdate=false).
 *  Everything under a built world is static at runtime — cutaway fades opacity,
 *  prop open-swaps replace CHILDREN of a kept group, ripple/sway displace
 *  vertices in-shader, culling toggles .visible — so the bake never goes stale,
 *  and children added later (chest swaps, dress parts) default back to auto and
 *  still work. EVERY buildWorld consumer that renders per frame should call this
 *  once the group is assembled (game.ts initWorld, the cinematic stage); anything
 *  that must MOVE at runtime belongs on the scene, never under the world group.
 *  `each` piggybacks extra per-object setup on the same traverse (game.ts tags
 *  the lamp occluder layer). */
export function freezeStatic(root: THREE.Object3D, each?: (o: THREE.Object3D) => void): void {
  root.traverse((o) => {
    each?.(o);
    o.updateMatrix();
    o.matrixAutoUpdate = false;
  });
}

export function buildWorld(map: MapDoc = loadedMap): THREE.Group {
  const seed = map.terrain.seed;
  // Refresh the active-map road list IN PLACE (onRoad + computeScatter read it).
  ROADS.length = 0;
  ROADS.push(...(map.roads as Road[]));
  const world = new THREE.Group();

  const dots: MapDot[] = [];
  const lights: LightSpot[] = [];
  const colliders: Collider[] = []; // solid decos that block player movement
  const surfaces: Surface[] = []; // walkable floors/stairs (structures)
  const placements: Placement[] = []; // instanced decos collected, built in one pass
  const culledDecos: CulledDeco[] = []; // non-instanced decos, distance-culled client-side
  const interactables: WorldInteractable[] = []; // props the player can interact with
  const propGroups = new Map<string, THREE.Group>(); // placed prop id → its mesh Group (client model swaps)

  // --- Phase 1: build structures FIRST so each can sit on the natural grade and
  // derive its own flatten pad + keep-out footprint. We must do this before the
  // terrain mesh (and deco scatter) so the pads are baked into heightAt().
  const builts = buildStructurePlacements(resolvePlacements(map.structures));

  // Publish the structure footprints as roam keep-outs (server reads these to keep
  // wildlife out of the castle/cottages). Rebuilt each call so a re-`buildWorld`
  // (HMR / re-seed) doesn't accumulate stale circles.
  structureKeepOuts.length = 0;
  for (const b of builts) structureKeepOuts.push({ x: b.placement.x, z: b.placement.z, r: b.footprint });

  // Dirt roads tint terrain verts (same mesh as grass) — must be set BEFORE
  // buildTerrain so chunks pick up the paint. City cobbles still come from path.ts.
  setTerrainRoadPaint(ROADS);
  world.add(buildTerrain(map.size));
  world.add(buildWater(map.size, true)); // cullDry: skip tiles whose terrain never nears the waterline

  // Keep deco scatter out of placed structures' footprints.
  const clearOf = (x: number, z: number) =>
    builts.some((b) => Math.hypot(x - b.placement.x, z - b.placement.z) < b.footprint);

  // Authored decos (the map doc's `decos` — chests + landmark placements).
  // Resolved before the scatter runs so it keeps a clearing around each one.
  const propSpecs = new Map(DECOS.map((d) => [d.type, d]));
  const props = map.decos.map((p) => ({ ...p, spec: propSpecs.get(p.type) }));
  const nearProp = (x: number, z: number) =>
    props.some((p) => Math.hypot(x - p.x, z - p.z) < (p.spec?.radius ?? 1) * (p.scale ?? 1) + 2);

  // Scatter decos on a jittered grid (computeScatter — the extracted loop the
  // studio world editor shares), then turn each decision into scene objects.
  const scatter = computeScatter(seed, {
    masks: hydrateMasks(map.scatterMasks ?? []),
    scatterBrush: map.scatterBrush ? hydrateBrush(map.scatterBrush) : undefined,
    palette: map.scatterPalette ? scatterPaletteById(map.scatterPalette)?.entries : undefined,
    size: map.size,
    clearOf,
    nearProp,
  });
  for (const sp of scatter) {
    const { spec, x, z } = sp;
    if (sp.iid) interactables.push({ id: sp.iid, spec, x, y: sp.y, z, scale: sp.scale, placed: false });

    if (NON_INSTANCED.has(spec.type)) {
      const deco = buildDeco(spec, sp.seed + 4);
      deco.position.set(x, sp.y, z);
      deco.rotation.y = sp.rot;
      deco.scale.setScalar(sp.scale);
      world.add(deco);
      lights.push({ x, y: sp.y + 2.4, z }); // wells glow at night (lifted off the ground)
      // Real mesh Groups (a draw call per part) — the client hides the whole
      // group past view distance (instanced.ts updateDecoView), pruning its
      // subtree from scene traversal + the shadow pass.
      culledDecos.push({ obj: deco, x, z, r: 8 });
    } else {
      placements.push({ spec, x, y: sp.y, z, rot: sp.rot, scale: sp.scale, variant: sp.variant, id: sp.iid });
    }

    // Solid models block movement — record a circular footprint scaled to match
    // the instance's visual size (see physics.ts). Tag with prop id when this
    // scatter instance is interactable so cut/regrow can shrink the solid.
    // A doc `height` BOUNDS the solid vertically (gravity plan: arcs can clear
    // it); `standable` adds a prop platform on its top (jump onto the rock).
    if (spec.solid) {
      const r = spec.radius * sp.scale;
      const band = spec.height !== undefined
        ? { minY: sp.y, maxY: sp.y + spec.height * sp.scale }
        : null;
      colliders.push({
        x, z, r,
        ...(band ?? {}),
        ...(sp.iid ? { id: sp.iid, baseR: r } : {}),
      });
      if (band && spec.standable) {
        const h = r * 0.707; // inscribed square over the round footprint
        surfaces.push({ kind: "platform", minX: x - h, maxX: x + h, minZ: z - h, maxZ: z + h, y: band.maxY, prop: true });
      }
    }

    dots.push({ x, z, color: spec.mapColor, kind: "deco" });
  }

  // Authored decos (chests + landmarks): real Groups — not instanced — so the
  // client can swap an interactable to its "-open" build. Both sides register
  // interactables under the AUTHORED id (the durable persistence key); only the
  // client keeps the Group (the headless server discards the scene). A deco
  // WITHOUT an interact block is a legitimate landmark placement — it renders
  // and collides but registers no interactable.
  for (const p of props) {
    const spec = p.spec;
    if (!spec) continue; // unknown type — validate.ts already errored
    const y = heightAt(p.x, p.z);
    const scale = p.scale ?? 1;
    // Build seed = the shared id→seed hash, so interact.ts's open-swap rebuild
    // reseeds identically.
    const deco = buildDeco(spec, variantSeed(p.id));
    deco.position.set(p.x, y, p.z);
    deco.rotation.y = p.rot ?? 0;
    deco.scale.setScalar(scale);
    world.add(deco);
    culledDecos.push({ obj: deco, x: p.x, z: p.z, r: 4 });
    if (spec.solid) {
      const r = spec.radius * scale;
      // Always tag authored solids with the placement id (chests, trees, …).
      // Same height-bound + standable-top rules as the scatter registration.
      const band = spec.height !== undefined
        ? { minY: y, maxY: y + spec.height * scale }
        : null;
      colliders.push({ x: p.x, z: p.z, r, ...(band ?? {}), id: p.id, baseR: r });
      if (band && spec.standable) {
        const h = r * 0.707;
        surfaces.push({ kind: "platform", minX: p.x - h, maxX: p.x + h, minZ: p.z - h, maxZ: p.z + h, y: band.maxY, prop: true });
      }
    }
    if (spec.interact) interactables.push({ id: p.id, spec, x: p.x, y, z: p.z, scale, placed: true });
    if (spec.type === "range") lights.push({ x: p.x, y: y + 1.2, z: p.z }); // the kitchen fire glows at night (plan 25)
    propGroups.set(p.id, deco);
    dots.push({ x: p.x, z: p.z, color: spec.mapColor, kind: "deco" });
  }

  world.add(buildInstanced(placements));

  // City: draped cobbles. Dirt: pebble accents only (ground brown is terrain paint).
  for (const road of ROADS) {
    world.add(buildPath(road.waypoints, road.width ?? 3.0, dots, road.style ?? "city"));
  }

  // Phase 2: add the structures built in phase 1 (walkable buildings with their
  // own collision + floors). Their lights go first so they win the night budget.
  const cutaways: { interior: InteriorInfo; cutaway: THREE.Mesh[] }[] = [];
  // Sittable furniture points (click-to-sit): every placement's seats, given
  // deterministic build-order ids. TRANSIENT — occupancy lives server-side per
  // session and ids may drift when structure content changes; NEVER persist
  // them (unlike npc/deco ids).
  const seats: ({ id: string } & Seat)[] = [];
  // Interactive doors (open/close): same build-order id rule as seats — NEVER
  // a persistence key (open state is session-only and resets closed on boot).
  const doors: ({ id: string } & StructureDoor)[] = [];
  let placementIdx = 0;
  for (const { built } of builts) {
    world.add(built.group);
    colliders.push(...built.colliders);
    surfaces.push(...built.surfaces);
    lights.unshift(...built.lights);
    dots.push(...built.dots.map((d) => ({ ...d, kind: "structure" as const })));
    cutaways.push({ interior: built.interior, cutaway: built.cutaway });
    built.seats.forEach((st, i) => seats.push({ id: `st:${placementIdx}:${i}`, ...st }));
    // Mint door ids + stamp matching closed-door box colliders (emit order).
    let di = 0;
    for (const d of built.doors) {
      const id = `dr:${placementIdx}:${di}`;
      doors.push({ id, ...d });
      di++;
    }
    di = 0;
    for (const c of built.colliders) {
      if ((c as BoxCollider).kind !== "box") continue;
      const b = c as BoxCollider;
      if (b.doorOpen === undefined) continue;
      const door = built.doors[di];
      if (door) b.doorId = `dr:${placementIdx}:${di}`;
      di++;
    }
    placementIdx++;
  }
  world.userData.cutaways = cutaways;
  world.userData.seats = seats;
  world.userData.doors = doors;

  // Publish the nav world so anything (remote players, etc.) can resolve heights.
  setWorldNav(colliders, surfaces);

  world.userData.dots = dots;
  world.userData.lights = lights;
  world.userData.culledDecos = culledDecos;
  world.userData.interactables = interactables;
  world.userData.propGroups = propGroups;
  world.userData.colliders = colliders;
  world.userData.surfaces = surfaces;
  world.userData.worldSize = map.size;
  return world;
}
